import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Star, Quote } from 'lucide-react';
// --- Components ---
const Section = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <section className={`mb-16 md:mb-24 ${className}`} data-id="element-2">{children}</section>;
const PullQuote = ({
  children
}: {
  children: React.ReactNode;
}) => <figure className="my-12 pl-6 border-l-4 border-[#7C9885]" data-id="element-3">
    <blockquote className="text-xl md:text-2xl font-serif italic text-gray-800 leading-relaxed" data-id="element-4">
      "{children}"
    </blockquote>
  </figure>;
const Subhead = ({
  children
}: {
  children: React.ReactNode;
}) => <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 mt-12 leading-tight" data-id="element-5">
    {children}
  </h2>;
const Paragraph = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <p className={`text-lg md:text-[20px] leading-[1.8] text-gray-700 mb-6 ${className}`} data-id="element-6">
    {children}
  </p>;
const CTAButton = ({
  text
}: {
  text: string;
}) => <div className="flex flex-col items-center my-12" data-id="element-7">
    <motion.a href="https://uitval1webinar.stresslessdogs.nl/" target="_blank" rel="noopener noreferrer" whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className="w-full md:w-auto px-8 py-4 bg-[#f05b56] text-white text-lg md:text-xl font-semibold rounded-xl shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 group" data-id="element-8">
      {text}
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-9" />
    </motion.a>
    <p className="mt-3 text-sm text-gray-500 font-medium" data-id="element-10">
      Gratis webinar. Beperkt aantal plekken per sessie.
    </p>
    <p className="mt-1 text-xs text-[#7C9885] uppercase tracking-wider font-semibold" data-id="element-11">
      Zonder straffen. Zonder dwang.
    </p>
  </div>;
const TestimonialCard = ({
  name,
  quote
}: {
  name: string;
  quote: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.5
}} className="bg-[#F8F7F5] p-8 rounded-2xl shadow-sm border border-stone-100" data-id="element-12">
    <div className="flex gap-1 mb-4 text-[#7C9885]" data-id="element-13">
      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-14" />)}
    </div>
    <p className="text-lg text-gray-700 italic mb-6 leading-relaxed" data-id="element-15">
      "{quote}"
    </p>
    <p className="font-medium text-gray-900" data-id="element-16">— {name}</p>
  </motion.div>;
// --- Main Page ---
export function LandingPage() {
  return <div className="min-h-screen bg-[#FDFCFA] font-sans text-gray-800 selection:bg-[#7C9885] selection:text-white" data-id="element-17">
      <main className="max-w-[720px] mx-auto px-6 pt-12 md:pt-20 pb-32" data-id="element-18">
        {/* 1. Hero Section */}
        <header className="mb-16 text-center md:text-left" data-id="element-19">
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 leading-[1.15] mb-8 tracking-tight" data-id="element-20">
            Na 3 jaar tijdens iedere wandeling uitvallen loopt Bella nu rustig
            langs andere honden. Het veranderde toen haar baasje met deze aanpak
            startte.
          </h1>
        </header>

        {/* 2. Opening Story */}
        <Section data-id="element-21">
          <Paragraph className="first-letter:text-5xl first-letter:font-bold first-letter:text-gray-900 first-letter:mr-3 first-letter:float-left" data-id="element-22">
            Marion zag de labrador al van verre aankomen.
          </Paragraph>
          <Paragraph data-id="element-23">
            Automatisch spande ze de lijn aan. Haar ademhaling versnelde. Ze
            keek om zich heen – kon ze nog oversteken? Nee, te laat.
          </Paragraph>
          <Paragraph data-id="element-24">En toen ging het zoals het altijd ging.</Paragraph>
          <Paragraph data-id="element-25">
            Bella ontplofte. Blaffen, grommen, happen naar de lucht. Marion trok
            uit alle macht aan de lijn terwijl de andere hondenbezitter snel
            voorbijliep met een blik die ze inmiddels zo goed kende. Die
            mengeling van afkeuring en medelijden.
          </Paragraph>
          <Paragraph data-id="element-26">
            "Sorry," mompelde Marion. Zoals ze al honderden keren had gemompeld.
          </Paragraph>
          <Paragraph data-id="element-27">
            Drie jaar lang was dit haar werkelijkheid. Elke wandeling hetzelfde
            ritueel: scannen, analyseren, anticiperen. Waar zijn andere honden?
            Kan ik uitwijken? Hoe kort moet ik de lijn houden?
          </Paragraph>
          <Paragraph data-id="element-28">
            Wandelen was geen ontspanning meer. Het was overleven.
          </Paragraph>
        </Section>

        {/* 3. Problem Section */}
        <Section data-id="element-29">
          <Subhead data-id="element-30">Het ergste was niet de schaamte</Subhead>
          <Paragraph data-id="element-31">
            Natuurlijk, de blikken van anderen deden pijn. Het gemompel. De
            buurman die een keer zei dat ze "haar hond beter onder controle
            moest krijgen."
          </Paragraph>
          <Paragraph data-id="element-32">Maar het ergste was iets anders.</Paragraph>
          <Paragraph data-id="element-33">
            Het was die gedachte die Marion steeds vaker door haar hoofd
            spookte. De gedachte die ze niet hardop durfde uit te spreken, zelfs
            niet tegen haar beste vriendin.
          </Paragraph>

          <PullQuote data-id="element-34">Kan ik dit nog wel aan?</PullQuote>

          <Paragraph data-id="element-35">
            Want ze hield van Bella. Écht. Thuis was Bella de liefste hond die
            je je kon voorstellen. Speels, aanhankelijk, vrolijk. Ze kroop 's
            avonds tegen Marion aan op de bank. Ze was dol op knuffelen.
          </Paragraph>
          <Paragraph data-id="element-36">Maar buiten veranderde ze in een ander dier.</Paragraph>
          <Paragraph data-id="element-37">
            Alsof er een schakelaar omging zodra ze een andere hond zag. Alsof
            de lieve Bella tijdelijk verdween en er een woeste vreemde voor in
            de plaats kwam.
          </Paragraph>
          <Paragraph data-id="element-38">
            Marion begreep het niet. En dat maakte het zo moeilijk.
          </Paragraph>
        </Section>

        {/* 4. Already Tried Everything */}
        <Section data-id="element-39">
          <Subhead data-id="element-40">Ik heb echt álles geprobeerd</Subhead>
          <Paragraph data-id="element-41">
            Als iemand Marion vroeg of ze al iets aan het uitvalgedrag had
            gedaan, moest ze bijna lachen. Of huilen. Of allebei.
          </Paragraph>
          <Paragraph data-id="element-42">
            Ze had een puppycursus gedaan. Daarna een vervolgcursus. Toen een
            gedragstherapeut ingeschakeld. Ze had boeken gelezen,
            YouTube-video's gekeken, tips van vrienden geprobeerd.
          </Paragraph>
          <Paragraph data-id="element-43">
            Afleiden met worstjes. Corrigeren met een ruk aan de lijn. Negeren
            en doorlopen. Wegdraaien zodra ze een hond zag. Bella laten zitten
            en wachten tot de andere hond voorbij was.
          </Paragraph>
          <Paragraph data-id="element-44">
            Sommige dingen leken even te werken. Een dag. Soms twee. En dan, bij
            de volgende wandeling, ging Bella weer tekeer alsof er nooit iets
            was veranderd.
          </Paragraph>
          <Paragraph data-id="element-45">
            Eén trainer – die veel geld had gekost – zei uiteindelijk iets wat
            Marion nog lang achtervolgde:
          </Paragraph>
          <div className="bg-stone-100 p-6 rounded-lg my-8 italic text-gray-700 border-l-4 border-stone-300" data-id="element-46">
            "Dit is karaktereigenschap. Sommige honden zijn nou eenmaal
            reactiever dan andere. Je kunt het managen, maar het gaat nooit
            helemaal weg."
          </div>
          <Paragraph data-id="element-47">Marion begon het bijna te geloven.</Paragraph>
          <Paragraph data-id="element-48">
            Misschien was Bella gewoon zo. Misschien moest ze het accepteren.
            Misschien zou elke wandeling voor altijd een gevecht blijven.
          </Paragraph>
        </Section>

        {/* 5. Discovery Section */}
        <Section data-id="element-49">
          <Subhead data-id="element-50">Wat Marion nog niet wist</Subhead>
          <Paragraph data-id="element-51">
            Op een avond stuurde een vriendin haar een link. "Heb je dit al
            gezien? Een{' '}
            <a href="https://uitval1webinar.stresslessdogs.nl/" target="_blank" rel="noopener noreferrer" className="text-[#f05b56] hover:underline font-medium" data-id="element-52">
              webinar
            </a>{' '}
            over uitvalgedrag. Misschien is het iets voor jullie."
          </Paragraph>
          <Paragraph data-id="element-53">
            Marion klikte zonder veel verwachting. Ze had al zóveel gelezen en
            gezien. Wat zou dit anders zijn?
          </Paragraph>
          <Paragraph data-id="element-54">
            Maar terwijl ze keek, begon er iets te verschuiven.
          </Paragraph>
          <Paragraph data-id="element-55">
            De gedragsdeskundige in de video zei iets wat Marion nooit eerder zo
            had gehoord:
          </Paragraph>

          <PullQuote data-id="element-56">
            Uitvallen is geen agressie. En het heeft ook niets met dominantie te
            maken. Het is een noodkreet. Een teken van stress.
          </PullQuote>

          <Paragraph data-id="element-57">Marion fronste. Stress?</Paragraph>
          <Paragraph data-id="element-58">
            "Een hond die uitvalt weet simpelweg niet wat hij anders moet doen.
            Hij voelt zich onveilig, overweldigd, overprikkeld – en uitvallen is
            de enige manier die hij kent om met die gevoelens om te gaan."
          </Paragraph>
          <Paragraph data-id="element-59">Dit was nieuw.</Paragraph>
          <Paragraph data-id="element-60">
            Geen trainer had het ooit zo uitgelegd. Iedereen had het gehad over
            "grenzen stellen," "de baas zijn," "niet toegeven aan het gedrag."
            Maar niemand had gevraagd: waarom doet Bella dit eigenlijk?
          </Paragraph>
          <Paragraph data-id="element-61">En toen kwam het inzicht dat alles veranderde.</Paragraph>
        </Section>

        {/* 6. Key Insight Section (Bucket Analogy) */}
        <Section data-id="element-62">
          <div className="bg-[#F0F4F1] p-8 md:p-10 rounded-2xl my-10" data-id="element-63">
            <h3 className="text-2xl font-bold text-[#2D4A3E] mb-6" data-id="element-64">
              De emmer die allang was overgelopen
            </h3>
            <Paragraph className="text-[#3A4D42]" data-id="element-65">
              De gedragsdeskundige legde tijdens{' '}
              <a href="https://uitval1webinar.stresslessdogs.nl/" target="_blank" rel="noopener noreferrer" className="text-[#f05b56] hover:underline font-medium" data-id="element-66">
                het webinar
              </a>{' '}
              uit dat uitvalgedrag nooit zomaar uit het niets komt. Het lijkt
              misschien zo – de ene seconde loopt je hond normaal, de volgende
              seconde ontploft hij. Maar dat is niet wat er werkelijk gebeurt.
            </Paragraph>
            <Paragraph className="text-[#3A4D42]" data-id="element-67">
              In werkelijkheid bouwt de spanning al veel eerder op.
            </Paragraph>
            <Paragraph className="text-[#3A4D42]" data-id="element-68">
              De strakke blik. De gespannen spieren. De manier waarop je hond al
              vanaf 50 meter fixeert op die andere hond in de verte. Het trekken
              aan de lijn. De versnelde ademhaling.
            </Paragraph>
            <Paragraph className="text-[#3A4D42] font-medium" data-id="element-69">
              Dit zijn geen onschuldige details. Dit is communicatie. Je hond
              vertelt je: "Ik voel de spanning oplopen. Mijn emmer raakt vol."
            </Paragraph>
            <Paragraph className="text-[#3A4D42] mb-0" data-id="element-70">
              En als niemand ingrijpt – als die signalen worden gemist – dan
              loopt de emmer over. En dát is het moment van uitvallen.
            </Paragraph>
          </div>

          <CTAButton text="Ja, ik wil begrijpen waarom mijn hond uitvalt →" data-id="element-71" />

          <Paragraph data-id="element-72">
            Marion realiseerde zich iets pijnlijks: ze had al die jaren naar het
            verkeerde moment gekeken. Ze had geprobeerd het uitvallen te stoppen
            op het moment dat het al te laat was. Terwijl de echte oplossing
            veel eerder lag.
          </Paragraph>
        </Section>

        {/* 7. Solution Section */}
        <Section data-id="element-73">
          <Subhead data-id="element-74">Drie dingen die Marion anders ging doen</Subhead>
          <Paragraph data-id="element-75">
            Na het{' '}
            <a href="https://uitval1webinar.stresslessdogs.nl/" target="_blank" rel="noopener noreferrer" className="text-[#f05b56] hover:underline font-medium" data-id="element-76">
              webinar
            </a>{' '}
            meldde Marion zich aan voor de anti-uitval cursus, die werd met
            korting tijdens het{' '}
            <a href="https://uitval1webinar.stresslessdogs.nl/" target="_blank" rel="noopener noreferrer" className="text-[#f05b56] hover:underline font-medium" data-id="element-77">
              webinar
            </a>{' '}
            aangeboden. Niet omdat ze nog zoveel vertrouwen had – dat was al
            vaak beschaamd. Maar omdat deze aanpak fundamenteel anders voelde.
            Logischer. Vriendelijker.
          </Paragraph>
          <Paragraph data-id="element-78">Ze leerde drie dingen:</Paragraph>

          <div className="space-y-10 mt-8" data-id="element-79">
            <div className="flex gap-4" data-id="element-80">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#7C9885] text-white flex items-center justify-center font-bold text-xl" data-id="element-81">
                1
              </div>
              <div data-id="element-82">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-83">
                  Herkennen wanneer de spanning opbouwt
                </h3>
                <Paragraph className="mb-0" data-id="element-84">
                  Marion leerde Bella's lichaamstaal lezen. Echt lezen. Niet
                  alleen de grote, duidelijke signalen zoals blaffen en grommen,
                  maar de subtiele voortekenen. De strakke mondhoeken. Het
                  fixeren met de ogen. De houding die nét iets te gespannen was.
                  Dit waren de momenten waarop Bella zei: "Het wordt me te
                  veel."
                </Paragraph>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-85">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#7C9885] text-white flex items-center justify-center font-bold text-xl" data-id="element-86">
                2
              </div>
              <div data-id="element-87">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-88">
                  Ingrijpen op het juiste moment
                </h3>
                <Paragraph className="mb-0" data-id="element-89">
                  Niet te laat – als de emmer al overloopt en Bella al aan het
                  uitvallen is. Dan is praten tegen een muur. Maar ook niet te
                  vroeg – als er nog helemaal niks aan de hand is. Dan snapt
                  Bella niet waarom je ingrijpt. Het gaat om die tussenzone. Het
                  moment waarop de spanning oploopt maar de explosie nog niet is
                  gebeurd. Dát is wanneer je je hond kunt helpen.
                </Paragraph>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-90">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#7C9885] text-white flex items-center justify-center font-bold text-xl" data-id="element-91">
                3
              </div>
              <div data-id="element-92">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-93">
                  Bella's stress structureel verlagen
                </h3>
                <Paragraph className="mb-0" data-id="element-94">
                  Dit was misschien wel de grootste eye-opener. Marion besefte
                  dat Bella niet alleen buiten gestrest was. Ze liep eigenlijk
                  de hele dag al met een halfvolle emmer rond. De drukte in
                  huis. De prikkels in de tuin. Het gebrek aan echte rust. Al
                  die kleine dingen vulden Bella's stressemmer, waardoor er
                  buiten maar weinig nodig was om hem te laten overlopen. Door
                  thuis meer rust te creëren, ging Bella met een leger emmer
                  naar buiten. En dat maakte alle verschil.
                </Paragraph>
              </div>
            </div>
          </div>

          <CTAButton text="Ja, ik wil begrijpen waarom mijn hond uitvalt →" data-id="element-95" />
        </Section>

        {/* 8. Transformation Section */}
        <Section data-id="element-96">
          <Subhead data-id="element-97">De eerste keer dat het lukte</Subhead>
          <Paragraph data-id="element-98">De eerste weken waren wennen.</Paragraph>
          <Paragraph data-id="element-99">
            Niet zozeer voor Bella – maar voor Marion. Zij moest zichzelf
            trainen om anders te kijken. Om niet automatisch de lijn aan te
            spannen zodra ze een hond zag. Om te letten op signalen die ze
            jarenlang had gemist.
          </Paragraph>
          <Paragraph data-id="element-100">
            Het was confronterend. Marion zag nu hoeveel stress Bella eigenlijk
            had. Stress die ze al die jaren niet had herkend.
          </Paragraph>
          <Paragraph data-id="element-101">Maar langzaam begon er iets te verschuiven.</Paragraph>
          <Paragraph data-id="element-102">
            Bella werd rustiger. Niet van de ene op de andere dag – maar stapje
            voor stapje. De spanning die Marion nooit als spanning had herkend,
            begon te zakken.
          </Paragraph>
          <Paragraph data-id="element-103">
            En toen, op een doodgewone dinsdagmiddag, gebeurde het.
          </Paragraph>
          <Paragraph data-id="element-104">
            Ze liepen door het park. In de verte kwam een herder aan. Marion
            voelde haar hartslag versnellen – oude reflexen – maar ze hield zich
            aan het plan. Ze lette op Bella's signalen. Die waren gespannen,
            maar nog niet over de grens.
          </Paragraph>
          <Paragraph data-id="element-105">Ze bleven doorlopen.</Paragraph>
          <Paragraph data-id="element-106">
            De herder kwam dichterbij. Bella keek. Ze was alert, maar niet
            gefixeerd. Marion gaf haar ruimte.
          </Paragraph>
          <Paragraph data-id="element-107">En toen liepen ze voorbij.</Paragraph>

          <PullQuote data-id="element-108">Geen uitval. Geen explosie. Gewoon... lopen.</PullQuote>

          <Paragraph data-id="element-109">
            Marion moest stoppen. Ze voelde de tranen opkomen. Dit was de eerste
            keer in drie jaar dat dit was gelukt. Niet door Bella te dwingen,
            niet door te corrigeren, niet door haar af te leiden met worstjes.
          </Paragraph>
          <Paragraph data-id="element-110">
            Maar door te begrijpen wat er aan de hand was. En door op het juiste
            moment, op de juiste manier in te grijpen.
          </Paragraph>

          <CTAButton text="Ja, ik wil begrijpen waarom mijn hond uitvalt →" data-id="element-111" />
        </Section>

        {/* 9. Social Proof Section */}
        <Section data-id="element-112">
          <Subhead data-id="element-113">Dit is ook voor jou mogelijk</Subhead>
          <Paragraph data-id="element-114">Marion is niet de enige.</Paragraph>
          <Paragraph data-id="element-115">
            Meer dan{' '}
            <span className="font-bold text-[#7C9885]" data-id="element-116">
              15.000 hondenbezitters
            </span>{' '}
            pakten uitvalgedrag aan met de Stressless Dogs methode. Mensen die
            net als Marion jarenlang hadden geworsteld. Die dachten dat het
            nooit zou veranderen. Die bijna de hoop hadden opgegeven.
          </Paragraph>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-10" data-id="element-117">
            <TestimonialCard name="Brunhilde" quote="Na een nare ervaring met een hondentrainer was ikzelf ook héél onzeker geworden. De cursus heeft mij geholpen. Inmiddels is er ook een hondenbroertje bijgekomen. En het gaat super." data-id="element-118" />
            <TestimonialCard name="Bianca" quote="Dankzij de heldere uitleg zijn we bewuster geworden. Wandelen zonder stress is nog een uitdaging, maar we merken duidelijk vooruitgang en genieten steeds meer samen." data-id="element-119" />
            <div className="md:col-span-2" data-id="element-120">
              <TestimonialCard name="Blije hondenbaas" quote="We merkten eigenlijk meteen verschil na het toepassen van een aantal vaardigheden. Er is veel meer rust in haar gedrag." data-id="element-121" />
            </div>
          </div>
        </Section>

        {/* 10. Webinar Introduction */}
        <Section data-id="element-122">
          <Subhead data-id="element-123">Ontdek het zelf – gratis</Subhead>
          <Paragraph data-id="element-124">
            Gedragsdeskundige Amee de Bruijn legt in een gratis{' '}
            <a href="https://uitval1webinar.stresslessdogs.nl/" target="_blank" rel="noopener noreferrer" className="text-[#f05b56] hover:underline font-medium" data-id="element-125">
              webinar
            </a>{' '}
            uit hoe uitvalgedrag ontstaat, waarom de meeste aanpakken niet
            werken, en welke 6-stappen methode wél resultaat geeft.
          </Paragraph>

          <div className="bg-white border border-stone-200 rounded-2xl p-8 my-10 shadow-sm" data-id="element-126">
            <h3 className="text-xl font-bold mb-6" data-id="element-127">
              In het webinar leer je onder andere:
            </h3>
            <ul className="space-y-4" data-id="element-128">
              {['Het onderliggende systeem dat ervoor zorgt dat je hond uitvalt – en hoe je dit doorbreekt', 'Hoe je een uitval kunt zien aankomen, zodat je op tijd kunt ingrijpen', 'Welke misverstanden uitvalgedrag juist in stand houden (en het probleem ongemerkt erger maken)', 'De eerste onmisbare stap om uitvalgedrag te stoppen – voor iedere vorm van uitvalgedrag', 'De 6-fasen methode die wij gebruiken om uitvallen echt, en in de kern, op te lossen'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-129">
                  <Check className="w-6 h-6 text-[#7C9885] flex-shrink-0 mt-0.5" data-id="element-130" />
                  <span className="text-gray-700 text-lg" data-id="element-131">{item}</span>
                </li>)}
            </ul>
          </div>

          <CTAButton text="Ja, ik wil begrijpen waarom mijn hond uitvalt →" data-id="element-132" />
        </Section>

        {/* 11. Final CTA Section */}
        <Section className="mb-0" data-id="element-133">
          <Subhead data-id="element-134">Kijk het gratis webinar</Subhead>
          <Paragraph data-id="element-135">
            Het{' '}
            <a href="https://uitval1webinar.stresslessdogs.nl/" target="_blank" rel="noopener noreferrer" className="text-[#f05b56] hover:underline font-medium" data-id="element-136">
              webinar
            </a>{' '}
            duurt 90 minuten en is op meerdere tijdstippen beschikbaar.
          </Paragraph>
          <Paragraph data-id="element-137">
            Misschien ben jij, net als Marion, al jaren aan het worstelen.
            Misschien denk je dat het nooit zal veranderen. Misschien heeft
            iemand je verteld dat het "karaktereigenschap" is.
          </Paragraph>
          <Paragraph className="font-bold text-gray-900" data-id="element-138">
            Dat hoeft niet waar te zijn.
          </Paragraph>
          <Paragraph data-id="element-139">
            Uitvalgedrag is omkeerbaar. Niet door harder te straffen, maar door
            te begrijpen wat je hond nodig heeft.
          </Paragraph>

          <CTAButton text="Ja, ik wil begrijpen waarom mijn hond uitvalt →" data-id="element-140" />
        </Section>
      </main>

      {/* Footer */}
      <footer className="bg-stone-100 py-12 text-center text-stone-500 text-sm" data-id="element-141">
        <div className="max-w-[720px] mx-auto px-6" data-id="element-142">
          <p data-id="element-143">
            © {new Date().getFullYear()} StresslessDogs. Alle rechten
            voorbehouden.
          </p>
          <p className="mt-2" data-id="element-144">
            Disclaimer: Resultaten kunnen variëren per hond en situatie.
          </p>
        </div>
      </footer>
    </div>;
}